/**
 * プロジェクト計画用スプリント列の定義
 * Feature Flag (sprints-multi-select) に応じて単一/複数選択を切り替える
 */
import { ValueSetterParams } from 'ag-grid-community'
import { useCallback, useMemo } from 'react'
import { ServerSideSelectFilter } from '../../../containers/BulkSheetView/components/filter'
import { SprintDetail } from '../../../../lib/functions/sprint'
import objects from '../../../../utils/objects'
import {
  useSprint,
  useSprints,
} from '../../../page-properties/bulksheet-properties/wbsItem'
import { ProjectPlanNewRow } from '../projectPlanNew'
import {
  getSprintFilterLabel,
  sortSprintsForFilter,
} from '../../../page-properties/bulksheet-properties/sprint/sprintFilterUtils'

// フィールド定数
export const FIELD_SPRINT = 'body.wbsItem.sprint'
export const FIELD_SPRINTS = 'body.wbsItem.sprints'
export const FIELD_WBSITEM_SPRINTS = 'wbsItem.sprints'

type UseSprintColumnParams = {
  getWbsItemProjectUuid: (
    row: ProjectPlanNewRow | undefined
  ) => string | undefined
  getTeamUuid: (row: ProjectPlanNewRow | undefined) => string | undefined
  useSprintsMultiSelect?: boolean
  /** フィルター実行時のAPI呼び出し */
  fetch: (v: any) => Promise<any>
  /** ドリルダウン時にフィルター選択肢を制限する関数 */
  getValuesForOptionFilter: (context: any) => Promise<string[]>
}

/** ServerSideSelectFilter用のフィルターパラメータを生成 */
const createServerSideFilterParams = (
  field: string,
  fetch: (v: any) => Promise<any>,
  getValuesForOptionFilter: (context: any) => Promise<string[]>
) => ({
  optionKey: 'sprint',
  valueGetter: ({ node }: { node: any }) => {
    const val = objects.getValue(node.data, field)
    return typeof val === 'string' ? undefined : val
  },
  getValue: (option: SprintDetail) =>
    option && option.uuid ? option.uuid : '',
  getLabel: getSprintFilterLabel,
  sortValues: (_: any, options?: SprintDetail[]) => {
    if (!options) return options
    return sortSprintsForFilter(options)
  },
  fetch,
  getValuesForOptionFilter,
})

/**
 * プロジェクト計画用スプリント列を返すフック
 * Feature Flagに応じて単一選択/複数選択を切り替える
 * ServerSideSelectFilterを使用
 */
export const useSprintColumn = ({
  getWbsItemProjectUuid,
  getTeamUuid,
  useSprintsMultiSelect,
  fetch,
  getValuesForOptionFilter,
}: UseSprintColumnParams) => {
  const sprintColSingle = useSprint({
    field: FIELD_SPRINT,
    getWbsItemProjectUuid,
    getTeamUuid,
    hide: false,
  })

  const sprintColMultiBase = useSprints<ProjectPlanNewRow>({
    field: FIELD_SPRINTS,
    editable: true,
    getWbsItemProjectUuid,
    getTeamUuid,
  })

  const sprintCol = useMemo(() => {
    const baseCol = useSprintsMultiSelect ? sprintColMultiBase : sprintColSingle

    // ServerSideSelectFilterを使用
    return {
      ...baseCol,
      filter: ServerSideSelectFilter,
      floatingFilter: true,
      filterParams: createServerSideFilterParams(
        useSprintsMultiSelect ? FIELD_SPRINTS : FIELD_SPRINT,
        fetch,
        getValuesForOptionFilter
      ),
    }
  }, [
    useSprintsMultiSelect,
    sprintColMultiBase,
    sprintColSingle,
    fetch,
    getValuesForOptionFilter,
  ])

  return sprintCol
}

/**
 * チーム変更時にスプリントをクリアするコールバックを返すフック
 */
export const useClearSprintOnTeamChange = (useSprintsMultiSelect?: boolean) => {
  const clearSprintOnTeamChange = useCallback(
    (params: ValueSetterParams<ProjectPlanNewRow>) => {
      const oldTeam = params.oldValue
      const newTeam = params.newValue

      if (useSprintsMultiSelect) {
        // 複数スプリント選択モード
        if (
          oldTeam?.uuid !== newTeam?.uuid &&
          params.data.body?.wbsItem?.sprints?.length
        ) {
          if (!params.data.editedData) {
            params.data.editedData = {
              [FIELD_SPRINTS]: params.data.body.wbsItem.sprints,
            }
          } else if (!params.data.editedData.hasOwnProperty(FIELD_SPRINTS)) {
            params.data.editedData[FIELD_SPRINTS] =
              params.data.body.wbsItem.sprints
          }
          params.data.body.wbsItem.sprints = []
        }
      } else {
        // 単一スプリント選択モード
        if (
          oldTeam?.uuid !== newTeam?.uuid &&
          !!params.data.body?.wbsItem?.sprint
        ) {
          if (!params.data.editedData) {
            params.data.editedData = {
              [FIELD_SPRINT]: params.data.body.wbsItem.sprint,
            }
          } else if (!params.data.editedData.hasOwnProperty(FIELD_SPRINT)) {
            params.data.editedData[FIELD_SPRINT] =
              params.data.body.wbsItem.sprint
          }
          params.data.body.wbsItem.sprint = undefined
        }
      }
    },
    [useSprintsMultiSelect]
  )

  return clearSprintOnTeamChange
}
